import { useEffect } from "react";

// ─── Generosity v3.2 palette ──────────────────────────────────────────────────
const C = {
  navy: "#0F1419",
  textSecondary: "#3D4043",
  muted: "#A6A8AB",
  blue: "#51B0E6",
  blueDark: "#1F6FA0",
  border: "rgba(15, 20, 25, 0.06)",
  panel: "#FFFFFF",
};

// ─── Self-contained stroke icons ───────────────────────────────────────────────
function DIcon({ name, size = 20, color = C.muted }) {
  const s = size, ic = color;
  const icons = {
    profile: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke={ic} strokeWidth="1.5"/><path d="M4 20c0-3.5 3.6-6 8-6s8 2.5 8 6" stroke={ic} strokeWidth="1.5" strokeLinecap="round"/></svg>,
    health: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 12h3l2-5 3 11 2.5-7 1.5 3H21" stroke={ic} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    intake: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 3.5c3.5 4 6 7 6 10a6 6 0 1 1-12 0c0-3 2.5-6 6-10Z" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/></svg>,
    devices: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.5" stroke={ic} strokeWidth="1.5"/><circle cx="12" cy="12" r="2.5" stroke={ic} strokeWidth="1.5"/></svg>,
    reports: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="3" y="12" width="4" height="9" rx="1" stroke={ic} strokeWidth="1.5"/><rect x="10" y="6" width="4" height="15" rx="1" stroke={ic} strokeWidth="1.5"/><rect x="17" y="3" width="4" height="18" rx="1" stroke={ic} strokeWidth="1.5"/></svg>,
    settings: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><line x1="4" y1="7" x2="20" y2="7" stroke={ic} strokeWidth="1.5" strokeLinecap="round"/><line x1="4" y1="12" x2="20" y2="12" stroke={ic} strokeWidth="1.5" strokeLinecap="round"/><line x1="4" y1="17" x2="20" y2="17" stroke={ic} strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="7" r="2" fill={C.panel} stroke={ic} strokeWidth="1.5"/><circle cx="15" cy="12" r="2" fill={C.panel} stroke={ic} strokeWidth="1.5"/><circle cx="8" cy="17" r="2" fill={C.panel} stroke={ic} strokeWidth="1.5"/></svg>,
    shop: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M5 7h14l-1.2 10.5a2 2 0 0 1-2 1.8H8.2a2 2 0 0 1-2-1.8L5 7Z" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/><path d="M9 7a3 3 0 0 1 6 0" stroke={ic} strokeWidth="1.5" strokeLinecap="round"/></svg>,
    refer: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="4" y="9" width="16" height="11" rx="1.5" stroke={ic} strokeWidth="1.5"/><path d="M4 13h16" stroke={ic} strokeWidth="1.5"/><path d="M12 9V20" stroke={ic} strokeWidth="1.5"/><path d="M12 9C12 6 10 4 8 5c-1.6.8-1 3 1 4h3Zm0 0c0-3 2-5 4-4 1.6.8 1 3-1 4h-3Z" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/></svg>,
    help: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={ic} strokeWidth="1.5"/><path d="M9.5 9.5a2.5 2.5 0 0 1 4 1.8c0 1.5-2 1.8-2 3.2" stroke={ic} strokeWidth="1.5" strokeLinecap="round"/><circle cx="11.5" cy="17.5" r="0.9" fill={ic}/></svg>,
    close: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M6 6L18 18M18 6L6 18" stroke={ic} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  };
  return <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{icons[name] || null}</span>;
}

// Flat single-tap navigation. Per-screen section menus live ON each screen,
// not in the drawer, so every destination is one tap.
// onNavigate(screenId). Router handles: profile, health, intake, devices,
// reports, settings, shop, refer, help, signin. `ready:false` shows a "Soon" tag.
const ITEMS = [
  { id: "profile", label: "Profile", icon: "profile", ready: true },
  { id: "health", label: "My Health", icon: "health", ready: true },
  { id: "intake", label: "My Intake", icon: "intake", ready: true },
  { id: "devices", label: "Devices & Integrations", icon: "devices", ready: true },
  { id: "reports", label: "Reports & History", icon: "reports", ready: true },
  { id: "settings", label: "Settings", icon: "settings", ready: false },
  { id: "shop", label: "Shop", icon: "shop", ready: true },
  { id: "refer", label: "Refer a Friend", icon: "refer", ready: false },
  { id: "help", label: "Help", icon: "help", ready: true },
  { id: "signin", label: "Sign in", icon: "profile", ready: true },
];

export default function NavDrawer({ open, onClose, onNavigate, founderMode, onFounderLogout }) {
  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const go = (item) => {
    if (!item.ready) return; // scaffolded, no destination yet
    onNavigate && onNavigate(item.id);
    onClose && onClose();
  };

  return (
    <div
      data-testid="nav-drawer"
      style={{ position: "fixed", inset: 0, zIndex: 600, fontFamily: "Montserrat, -apple-system, sans-serif" }}
    >
      <style>{`
        @keyframes drawerFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes drawerSlide { from { transform: translateX(-100%) } to { transform: translateX(0) } }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "absolute", inset: 0, background: "rgba(15, 20, 25, 0.35)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", animation: "drawerFade 0.2s ease" }}
      />

      {/* Panel */}
      <div
        style={{
          position: "absolute", top: 0, left: 0, bottom: 0,
          width: "82%", maxWidth: 320,
          background: C.panel,
          boxShadow: "2px 0 40px rgba(15, 20, 25, 0.18)",
          display: "flex", flexDirection: "column",
          animation: "drawerSlide 0.28s cubic-bezier(0.34,1.4,0.64,1)",
          paddingTop: "env(safe-area-inset-top)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 18px 14px", borderBottom: `1px solid ${C.border}` }}>
          <img src="/generosity-logo.png" alt="Generosity Water Intelligence" style={{ height: 40, width: "auto" }} />
          <button onClick={onClose} aria-label="Close menu" style={{ width: 34, height: 34, borderRadius: "50%", background: "#F0F1F3", border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <DIcon name="close" size={16} color={C.muted} />
          </button>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: "auto", padding: "6px 0", WebkitOverflowScrolling: "touch" }}>
          {ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => go(item)}
              data-testid={`drawer-item-${item.id}`}
              disabled={!item.ready}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 14,
                padding: "15px 20px", background: "transparent",
                border: "none", borderBottom: `1px solid ${C.border}`,
                textAlign: "left", cursor: item.ready ? "pointer" : "default",
                opacity: item.ready ? 1 : 0.55, fontFamily: "inherit",
              }}
            >
              <DIcon name={item.icon} size={20} color={item.ready ? C.blueDark : C.muted} />
              <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: C.navy }}>{item.label}</span>
              {!item.ready && (
                <span style={{ fontSize: 9, fontWeight: 700, color: C.muted, letterSpacing: 0.6, textTransform: "uppercase", background: "rgba(15,20,25,0.04)", padding: "3px 8px", borderRadius: 10 }}>Soon</span>
              )}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 8 }}>
          <span style={{ fontSize: 10, color: C.muted, letterSpacing: 0.4 }}>Water Integrity. Insights. Intelligence.</span>
          {founderMode && (
            <button
              type="button"
              onClick={() => onFounderLogout && onFounderLogout()}
              title="Exit founder mode"
              style={{ fontSize: 9, fontWeight: 700, color: C.blueDark, letterSpacing: 0.6, textTransform: "uppercase", background: "rgba(81,176,230,0.08)", border: "1px solid rgba(81,176,230,0.18)", padding: "3px 8px", borderRadius: 10, cursor: "pointer" }}
            >
              Founder &middot; Exit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
